import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axiosInstance from '../api';
import './TestDetails.css';

// SOC 2 Section III system-description editor — same WYSIWYG toolbar the policy editor uses.
const SYS_DESC_QUILL = {
  toolbar: [
    [{ header: '1' }, { header: '2' }, { font: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['bold', 'italic', 'underline'],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
};

const EV_STATUS_COLORS = {
  REQUESTED: '#f59e0b', SUBMITTED: '#3b82f6', APPROVED: '#10b981', REJECTED: '#ef4444',
};
const REVIEW_COLORS = { PENDING: '#6b7280', ACCEPTED: '#10b981', REJECTED: '#ef4444' };
const TYPE_ICONS = { FILE: 'fa-file-alt', NOTE: 'fa-sticky-note', LINK: 'fa-external-link-alt' };

const RUN_STATUS = {
  PASS:    { bg: '#f0fdf4', color: '#16a34a', icon: 'fa-check-circle' },
  FAIL:    { bg: '#fef2f2', color: '#dc2626', icon: 'fa-times-circle' },
  FAILED:  { bg: '#fef2f2', color: '#dc2626', icon: 'fa-times-circle' },
  SKIPPED: { bg: '#fffbeb', color: '#d97706', icon: 'fa-minus-circle' },
  RUNNING: { bg: '#eff6ff', color: '#2563eb', icon: 'fa-circle-notch fa-spin' },
  PENDING: { bg: '#f8fafc', color: '#64748b', icon: 'fa-clock' },
  ERROR:   { bg: '#fef2f2', color: '#dc2626', icon: 'fa-exclamation-circle' },
};

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][dt.getMonth()];
  const day = String(dt.getDate()).padStart(2, '0');
  const hh  = dt.getHours(), mm = String(dt.getMinutes()).padStart(2, '0');
  return `${mon} ${day}, ${hh % 12 || 12}:${mm} ${hh >= 12 ? 'PM' : 'AM'}`;
}

function RunStatusBadge({ status }) {
  const s = (status || '').toUpperCase();
  const t = RUN_STATUS[s] || { bg: '#f8fafc', color: '#64748b', icon: 'fa-circle' };
  return (
    <span className="td-run-badge" style={{ background: t.bg, color: t.color }}>
      <i className={`fas ${t.icon}`} /> {status || '—'}
    </span>
  );
}

function TestDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const role    = localStorage.getItem('role');
  const isAdmin = role === 'ROLE_ADMIN';

  const [test, setTest]               = useState(null);
  const [history, setHistory]         = useState([]);
  const [error, setError]             = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages]   = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [activeTab, setActiveTab]     = useState('history');
  const [sysDesc, setSysDesc]         = useState('');
  const [sysDescSaving, setSysDescSaving] = useState(false);
  const [sysDescSaved, setSysDescSaved]   = useState(false);
  const [adminUrl, setAdminUrl]           = useState('');
  const [adminUrlSaving, setAdminUrlSaving] = useState(false);
  const [adminUrlSaved, setAdminUrlSaved]   = useState(false);
  const [expandedRun, setExpandedRun] = useState(null);      // testResultId whose per-asset findings are open
  const [findingFilter, setFindingFilter] = useState('all'); // all | fail | pass

  // Evidence state
  const [evRequests,  setEvRequests]  = useState([]);
  const [evLoading,   setEvLoading]   = useState(false);
  const [evExpanded,  setEvExpanded]  = useState({});
  const [evItems,     setEvItems]     = useState({});
  const [showCreate,  setShowCreate]  = useState(false);
  const [createForm,  setCreateForm]  = useState({ title: '', description: '', evidenceType: 'ANY', dueDate: '' });
  const [submitPanel, setSubmitPanel] = useState(null);  // requestId
  const [submitType,  setSubmitType]  = useState('NOTE');
  const [noteText,    setNoteText]    = useState('');
  const [linkUrl,     setLinkUrl]     = useState('');
  const [linkNote,    setLinkNote]    = useState('');
  const [reviewModal, setReviewModal] = useState(null);
  const [evSuccess,   setEvSuccess]   = useState('');
  const [evError,     setEvError]     = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [testRes, historyRes] = await Promise.all([
          axiosInstance.get(`/tests/${id}`),
          axiosInstance.get(`/test-result/${id}/history`, {
            params: { page: currentPage - 1, size: itemsPerPage },
          }),
        ]);
        setTest(testRes.data);
        const hData = historyRes.data;
        if (hData && Array.isArray(hData.content)) {
          setHistory(hData.content); setTotalPages(hData.totalPages || 1); setTotalElements(hData.totalElements || 0);
        } else if (Array.isArray(hData)) {
          setHistory(hData[0]?.history || hData); setTotalPages(1); setTotalElements(hData.length);
        }
      } catch { setError(t('TestDetails.errorFetchingDetails')); }
    };
    fetchData();
  }, [id, currentPage, itemsPerPage, t]);

  const loadEvidence = useCallback(async () => {
    setEvLoading(true);
    try {
      const res = await axiosInstance.get(`/evidence/requests/test/${id}`);
      setEvRequests(res.data || []);
    } catch { setEvError(t('TestDetails.failedLoadEvidence')); }
    finally { setEvLoading(false); }
  }, [id, t]);

  // SOC 2 Section III: this test is authored via a rich-text editor, not evidence upload.
  const isSystemDescription = !!(test && /system description/i.test(test.testName || ''));
  // Controls CATAAM can satisfy automatically from iASM (network diagram, pen test) — offer the
  // 'run iASM to automate' path alongside the manual 3rd-party upload.
  const isIasmAutomatable = !!(test && /network diagram|penetration test|pen test|pentest/i.test(test.testName || ''));
  // Admin-console SSL/TLS control: manual until the org supplies its admin URL, then automated.
  const isAdminConsole = !!(test && /admin page of infrastructure console/i.test(test.testName || ''));

  useEffect(() => {
    if (!isSystemDescription) return;
    setActiveTab('systemDescription');
    axiosInstance.get('/org-profile')
      .then(r => setSysDesc(r.data?.systemDescription || ''))
      .catch(() => {});
  }, [isSystemDescription]);

  useEffect(() => {
    if (!isAdminConsole) return;
    setActiveTab('adminConsole');
    axiosInstance.get('/org-profile')
      .then(r => setAdminUrl(r.data?.adminConsoleUrl || ''))
      .catch(() => {});
  }, [isAdminConsole]);

  const saveSystemDescription = async () => {
    setSysDescSaving(true); setSysDescSaved(false);
    try {
      await axiosInstance.put('/org-profile/system-description', { systemDescription: sysDesc });
      setSysDescSaved(true);
    } catch (e) { /* handled by disabled state; user can retry */ }
    finally { setSysDescSaving(false); }
  };

  const saveAdminConsoleUrl = async () => {
    setAdminUrlSaving(true); setAdminUrlSaved(false);
    try {
      await axiosInstance.put('/org-profile/admin-console-url', { adminConsoleUrl: adminUrl });
      setAdminUrlSaved(true);
    } catch (e) { /* handled by disabled state; user can retry */ }
    finally { setAdminUrlSaving(false); }
  };

  useEffect(() => {
    if (activeTab === 'evidence') loadEvidence();
  }, [activeTab, loadEvidence]);

  const toggleEvExpand = async (requestId) => {
    const next = { ...evExpanded, [requestId]: !evExpanded[requestId] };
    setEvExpanded(next);
    if (next[requestId] && !evItems[requestId]) {
      try {
        const res = await axiosInstance.get(`/evidence/requests/${requestId}/items`);
        setEvItems(i => ({ ...i, [requestId]: res.data }));
      } catch { /* silent */ }
    }
  };

  const handleCreateRequest = async () => {
    if (!createForm.title.trim()) return;
    try {
      await axiosInstance.post('/evidence/requests/by-test', {
        testId: Number(id),
        ...createForm,
        dueDate: createForm.dueDate || null,
      });
      setShowCreate(false);
      setCreateForm({ title: '', description: '', evidenceType: 'ANY', dueDate: '' });
      setEvSuccess(t('TestDetails.evidenceRequestCreated'));
      loadEvidence();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || t('TestDetails.failedCreateEvidence');
      setEvError(msg);
    }
  };

  const handleNoteSubmit = async (requestId) => {
    if (!noteText.trim()) return;
    try {
      await axiosInstance.post(`/evidence/requests/${requestId}/items/note`, { notes: noteText });
      setNoteText(''); setSubmitPanel(null); setEvSuccess(t('TestDetails.noteSubmitted'));
      const res = await axiosInstance.get(`/evidence/requests/${requestId}/items`);
      setEvItems(i => ({ ...i, [requestId]: res.data }));
      loadEvidence();
    } catch { setEvError(t('TestDetails.failedSubmitNote')); }
  };

  const handleLinkSubmit = async (requestId) => {
    if (!linkUrl.trim()) return;
    try {
      await axiosInstance.post(`/evidence/requests/${requestId}/items/link`,
        { externalLink: linkUrl, notes: linkNote });
      setLinkUrl(''); setLinkNote(''); setSubmitPanel(null); setEvSuccess(t('TestDetails.linkSubmitted'));
      const res = await axiosInstance.get(`/evidence/requests/${requestId}/items`);
      setEvItems(i => ({ ...i, [requestId]: res.data }));
      loadEvidence();
    } catch { setEvError(t('TestDetails.failedSubmitLink')); }
  };

  const handleFileSubmit = async (requestId, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      await axiosInstance.post(`/evidence/requests/${requestId}/items/file`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setSubmitPanel(null); setEvSuccess(t('TestDetails.fileUploaded'));
      const res = await axiosInstance.get(`/evidence/requests/${requestId}/items`);
      setEvItems(i => ({ ...i, [requestId]: res.data }));
      loadEvidence();
    } catch { setEvError(t('TestDetails.failedUploadFile')); }
  };

  const handleReview = async () => {
    if (!reviewModal) return;
    try {
      await axiosInstance.put(`/evidence/items/${reviewModal.itemId}/review`, {
        reviewStatus: reviewModal.status, reviewNotes: reviewModal.notes,
      });
      setReviewModal(null); setEvSuccess(t('TestDetails.reviewSaved'));
      for (const reqId of Object.keys(evExpanded)) {
        if (evExpanded[reqId]) {
          const res = await axiosInstance.get(`/evidence/requests/${reqId}/items`);
          setEvItems(i => ({ ...i, [reqId]: res.data }));
        }
      }
      loadEvidence();
    } catch { setEvError(t('TestDetails.failedSaveReview')); }
  };

  const handleDownload = async (itemId, fileName) => {
    try {
      const res = await axiosInstance.get(`/evidence/items/${itemId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = fileName || `evidence-${itemId}`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { setEvError(t('TestDetails.downloadFailed')); }
  };

  if (error) return (
    <div style={{ padding: '1rem', color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, fontSize: '0.875rem' }}>
      {error}
    </div>
  );

  if (!test) return <div className="td-loading"><div className="td-spinner" /></div>;

  const startCount = totalElements ? (currentPage - 1) * itemsPerPage + 1 : '';
  const endCount   = totalElements ? Math.min(currentPage * itemsPerPage, totalElements) : '';

  const lastRun = history[0];

  return (
    <div className="test-details">
      <button className="td-back-btn" onClick={() => navigate(-1)}>
        <i className="fas fa-arrow-left" /> {t('TestDetails.back')}
      </button>

      {/* ── Header card ── */}
      <div className="td-header">
        <div className="td-header-top">
          <div className="td-header-badges">
            {test.testCategory && (
              <span className="td-category-pill">{test.testCategory}</span>
            )}
            {lastRun?.status && <RunStatusBadge status={lastRun.status} />}
          </div>
          {lastRun?.endDate && (
            <span className="td-last-run">
              <i className="fas fa-clock" /> {t('TestDetails.lastRun', { date: fmtDate(lastRun.endDate) })}
            </span>
          )}
        </div>
        <h2 className="td-test-name">{test.testName}</h2>
        {test.frameworkResponseDTOS?.length > 0 && (
          <div className="td-fw-tags">
            {test.frameworkResponseDTOS
              .filter((fw, i, arr) => arr.findIndex(f => f.displayName === fw.displayName) === i)
              .map((fw, i) => (
                <span key={i} className="td-fw-tag">{fw.displayName}</span>
              ))}
          </div>
        )}
      </div>

      {isIasmAutomatable && (
        <div style={{ background: '#eef4fb', border: '1px solid #cfe0f2', borderRadius: 8,
                      padding: '12px 14px', margin: '4px 0 14px' }}>
          <div style={{ fontSize: 13, color: '#25405a' }}>
            <strong>This control can be automated.</strong> CATAAM generates it from your iASM
            attack-surface data — connect and run iASM and it passes automatically. Using a
            third-party pentest instead? Upload the report as evidence and it will satisfy the control.
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/dashboard/iasm')}
              style={{ background: '#1f5b8f', color: '#fff', border: 'none', borderRadius: 6,
                       padding: '7px 14px', cursor: 'pointer', fontWeight: 600 }}>
              <i className="fas fa-bolt" /> Run iASM to automate
            </button>
            <button onClick={() => setActiveTab('evidence')}
              style={{ background: '#fff', color: '#1f5b8f', border: '1px solid #1f5b8f',
                       borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontWeight: 600 }}>
              <i className="fas fa-upload" /> Upload a 3rd-party report
            </button>
          </div>
        </div>
      )}

      {/* Tab switcher */}
      <div className="td-tabs">
        <button className={`td-tab${activeTab === 'history'  ? ' active' : ''}`}
          onClick={() => setActiveTab('history')}>
          <i className="fas fa-history" /> {t('TestDetails.executionHistory')}
        </button>
        <button className={`td-tab${activeTab === 'evidence' ? ' active' : ''}`}
          onClick={() => setActiveTab('evidence')}>
          <i className="fas fa-paperclip" /> {t('TestDetails.evidence')}
          {evRequests.length > 0 && <span className="td-tab-count">{evRequests.length}</span>}
        </button>
        {isSystemDescription && (
          <button className={`td-tab${activeTab === 'systemDescription' ? ' active' : ''}`}
            onClick={() => setActiveTab('systemDescription')}>
            <i className="fas fa-file-alt" /> System Description
          </button>
        )}
        {isAdminConsole && (
          <button className={`td-tab${activeTab === 'adminConsole' ? ' active' : ''}`}
            onClick={() => setActiveTab('adminConsole')}>
            <i className="fas fa-lock" /> Admin Console URL
          </button>
        )}
      </div>

      {/* ── System Description Tab (SOC 2 Section III) ── */}
      {activeTab === 'systemDescription' && isSystemDescription && (
        <div style={{ padding: '8px 4px' }}>
          <p style={{ color: '#52616b', fontSize: 13, marginBottom: 10 }}>
            Describe your system for SOC 2 Section III. It is saved to your organization, shown as the
            heading/overview on your Trust Center, and saving it satisfies this control automatically.
          </p>
          <ReactQuill theme="snow" value={sysDesc} onChange={setSysDesc} modules={SYS_DESC_QUILL} />
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={saveSystemDescription} disabled={sysDescSaving}
              style={{ background: '#1f5b8f', color: '#fff', border: 'none', borderRadius: 6,
                       padding: '8px 16px', cursor: sysDescSaving ? 'default' : 'pointer', fontWeight: 600 }}>
              {sysDescSaving ? 'Saving…' : 'Save system description'}
            </button>
            {sysDescSaved && <span style={{ color: '#2e7d32', fontWeight: 600 }}><i className="fas fa-check" /> Saved</span>}
          </div>
        </div>
      )}

      {/* ── Admin Console URL Tab ── */}
      {activeTab === 'adminConsole' && isAdminConsole && (
        <div style={{ padding: '8px 4px' }}>
          <p style={{ color: '#52616b', fontSize: 13, marginBottom: 10 }}>
            Enter the URL of your infrastructure <strong>admin console / management web app</strong> (e.g. your
            internal admin panel). This is separate from your public company website (covered by
            “SSL/TLS enforced on company website”). Once saved, this control becomes <strong>automated</strong> —
            Cataam validates that the admin interface enforces TLS/SSL on each run.
          </p>
          <input type="url" value={adminUrl} onChange={e => setAdminUrl(e.target.value)}
            placeholder="https://admin.yourcompany.com"
            style={{ width: '100%', maxWidth: 480, padding: '9px 12px', border: '1px solid #cdd6dd',
                     borderRadius: 6, fontSize: 14 }} />
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button onClick={saveAdminConsoleUrl} disabled={adminUrlSaving}
              style={{ background: '#1f5b8f', color: '#fff', border: 'none', borderRadius: 6,
                       padding: '8px 16px', cursor: adminUrlSaving ? 'default' : 'pointer', fontWeight: 600 }}>
              {adminUrlSaving ? 'Saving…' : 'Save admin console URL'}
            </button>
            {adminUrlSaved && (
              <span style={{ color: '#2e7d32', fontWeight: 600 }}>
                <i className="fas fa-check" /> Saved — control is now automated. Run the test to validate.
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── History Tab ── */}
      {activeTab === 'history' && (
        <div className="table-container">
          {history.length === 0 ? (
            <p>{t('TestDetails.noTestHistory')}</p>
          ) : (
            <table className="history-table">
              <thead>
                <tr>
                  <th style={{ width: 26 }} />
                  <th>{t('TestDetails.startDate')}</th><th>{t('TestDetails.endDate')}</th><th>{t('TestDetails.status')}</th>
                  <th>{t('TestDetails.reason')}</th><th>{t('TestDetails.assetCoverage')}</th><th>{t('TestDetails.executioner')}</th>
                </tr>
              </thead>
              <tbody>
                {history.map(item => {
                  const findings = item.findings || [];
                  const hasFindings = findings.length > 0;
                  const isOpen = expandedRun === item.testResultId;
                  const shown = findingFilter === 'all'
                    ? findings
                    : findings.filter(f => (f.status || '').toUpperCase() === findingFilter.toUpperCase());
                  return (
                    <React.Fragment key={item.testResultId}>
                      <tr
                        className={`td-hist-row${hasFindings ? ' td-hist-clickable' : ''}${isOpen ? ' td-hist-open' : ''}`}
                        onClick={() => hasFindings && setExpandedRun(isOpen ? null : item.testResultId)}
                      >
                        <td className="td-chevron">
                          {hasFindings && <i className={`fas fa-chevron-${isOpen ? 'down' : 'right'}`} />}
                        </td>
                        <td className="td-date">{fmtDate(item.startDate)}</td>
                        <td className="td-date">{fmtDate(item.endDate)}</td>
                        <td><RunStatusBadge status={item.status} /></td>
                        <td className="td-reason">{item.comment || '—'}</td>
                        <td>
                          {hasFindings ? (
                            <span className="td-cov" title={t('TestDetails.perAssetResultsTitle')}>
                              {item.failedAssets > 0 && (
                                <span className="td-cov-fail"><i className="fas fa-times-circle" /> {t('TestDetails.assetsFailed', { count: item.failedAssets })}</span>
                              )}
                              <span className="td-cov-pass"><i className="fas fa-check-circle" /> {t('TestDetails.assetsPassed', { count: item.passedAssets })}</span>
                              <span className="td-cov-total">{t('TestDetails.ofTotal', { count: item.totalAssets })}</span>
                            </span>
                          ) : (
                            <span className="td-cov-na" title={t('TestDetails.orgLevelTitle')}>
                              {item.assets || t('TestDetails.orgLevel')}
                            </span>
                          )}
                        </td>
                        <td>{item.testExecutioner || t('TestDetails.system')}</td>
                      </tr>

                      {isOpen && (
                        <tr className="td-findings-row">
                          <td colSpan={7}>
                            <div className="td-findings">
                              <div className="td-findings-head">
                                <span className="td-findings-title">
                                  <i className="fas fa-layer-group" /> {t('TestDetails.perAssetResults')}
                                  <span className="td-findings-sub"> {t('TestDetails.oneRunAssetsEvaluated', { count: item.totalAssets })}</span>
                                </span>
                                <div className="td-findings-filter" onClick={e => e.stopPropagation()}>
                                  {['all', 'fail', 'pass'].map(f => (
                                    <button
                                      key={f}
                                      className={`td-ff-btn${findingFilter === f ? ' active' : ''}`}
                                      onClick={() => setFindingFilter(f)}
                                    >
                                      {f === 'all' ? t('TestDetails.filterAll', { count: item.totalAssets }) : f === 'fail' ? t('TestDetails.filterFailed', { count: item.failedAssets }) : t('TestDetails.filterPassed', { count: item.passedAssets })}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <table className="td-findings-table">
                                <thead>
                                  <tr><th>{t('TestDetails.asset')}</th><th>{t('TestDetails.type')}</th><th>{t('TestDetails.result')}</th><th>{t('TestDetails.detail')}</th></tr>
                                </thead>
                                <tbody>
                                  {shown.length === 0 ? (
                                    <tr><td colSpan={4} className="td-findings-empty">{t('TestDetails.noAssetsInRun', { filter: t(`TestDetails.filterWord_${findingFilter}`) })}</td></tr>
                                  ) : shown.map((f, i) => (
                                    <tr key={i}>
                                      <td className="td-f-asset">{f.assetRef || '—'}</td>
                                      <td><span className="td-f-type">{f.assetType || '—'}</span></td>
                                      <td><RunStatusBadge status={f.status} /></td>
                                      <td className="td-f-detail">{f.detail || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
          {totalElements > 0 && (
            <div className="pagination">
              <span className="pagination-info">{t('TestDetails.paginationInfo', { start: startCount, end: endCount, total: totalElements })}</span>
              <div className="pagination-controls">
                <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} className="pagination-button">&lt;</button>
                <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage >= totalPages} className="pagination-button">&gt;</button>
                <select value={itemsPerPage} onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="small-dropdown">
                  {[5, 10, 15, 20].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Evidence Tab ── */}
      {activeTab === 'evidence' && (
        <div className="td-evidence">
          {evError   && <div className="td-ev-alert td-ev-alert-err">{evError}<button onClick={() => setEvError('')}>&times;</button></div>}
          {evSuccess && <div className="td-ev-alert td-ev-alert-ok">{evSuccess}<button onClick={() => setEvSuccess('')}>&times;</button></div>}

          {/* Create button (admin only) */}
          {isAdmin && !showCreate && (
            <div className="td-ev-actions">
              <button className="td-ev-btn-primary" onClick={() => setShowCreate(true)}>
                <i className="fas fa-plus" /> {t('TestDetails.requestEvidence')}
              </button>
            </div>
          )}

          {/* Create form */}
          {showCreate && (
            <div className="td-ev-create-form">
              <h4>{t('TestDetails.newEvidenceRequest')}</h4>
              <div className="td-ev-field">
                <label>{t('TestDetails.titleLabel')}</label>
                <input className="td-ev-input"
                  placeholder={t('TestDetails.titlePlaceholder')}
                  value={createForm.title}
                  onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="td-ev-field">
                <label>{t('TestDetails.descriptionLabel')}</label>
                <textarea className="td-ev-textarea" rows={2}
                  placeholder={t('TestDetails.descriptionPlaceholder')}
                  value={createForm.description}
                  onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="td-ev-row">
                <div className="td-ev-field">
                  <label>{t('TestDetails.evidenceTypeLabel')}</label>
                  <select className="td-ev-input"
                    value={createForm.evidenceType}
                    onChange={e => setCreateForm(f => ({ ...f, evidenceType: e.target.value }))}>
                    <option value="ANY">{t('TestDetails.evidenceTypeAny')}</option>
                    <option value="FILE">{t('TestDetails.evidenceTypeFile')}</option>
                    <option value="NOTE">{t('TestDetails.evidenceTypeNote')}</option>
                    <option value="LINK">{t('TestDetails.evidenceTypeLink')}</option>
                  </select>
                </div>
                <div className="td-ev-field">
                  <label>{t('TestDetails.dueDateLabel')}</label>
                  <input className="td-ev-input" type="date"
                    value={createForm.dueDate}
                    onChange={e => setCreateForm(f => ({ ...f, dueDate: e.target.value }))} />
                </div>
              </div>
              <div className="td-ev-form-actions">
                <button className="td-ev-btn-primary" onClick={handleCreateRequest}>{t('TestDetails.createRequest')}</button>
                <button className="td-ev-btn-ghost" onClick={() => setShowCreate(false)}>{t('TestDetails.cancel')}</button>
              </div>
            </div>
          )}

          {/* Evidence request cards */}
          {evLoading ? (
            <div className="td-loading"><div className="td-spinner" /></div>
          ) : evRequests.length === 0 ? (
            <div className="td-ev-empty">
              <i className="fas fa-paperclip" />
              <p>{t('TestDetails.noEvidenceRequests')}</p>
              {isAdmin && <p className="td-ev-hint">{t('TestDetails.evidenceHint')}</p>}
            </div>
          ) : (
            <div className="td-ev-list">
              {evRequests.map(req => {
                const reqItems = evItems[req.id] || [];
                const isOpen   = !!evExpanded[req.id];
                const isSub    = submitPanel === req.id;

                return (
                  <div key={req.id} className="td-ev-card">
                    <div className="td-ev-card-header" onClick={() => toggleEvExpand(req.id)}>
                      <div className="td-ev-card-left">
                        <span className="td-ev-badge" style={{ background: EV_STATUS_COLORS[req.status] || '#6b7280' }}>
                          {req.status}
                        </span>
                        <div>
                          <div className="td-ev-card-title">{req.title}</div>
                          {req.description && <div className="td-ev-card-desc">{req.description}</div>}
                        </div>
                      </div>
                      <div className="td-ev-card-right">
                        {req.dueDate && <span className="td-ev-due"><i className="fas fa-calendar-alt" /> {req.dueDate}</span>}
                        <span className="td-ev-type">{req.evidenceType}</span>
                        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} td-ev-chevron`} />
                      </div>
                    </div>

                    {isOpen && (
                      <div className="td-ev-card-body">
                        {/* Items */}
                        {reqItems.length > 0 && (
                          <div className="td-ev-items">
                            <h5>{t('TestDetails.submittedEvidence', { count: reqItems.length })}</h5>
                            {reqItems.map(item => (
                              <div key={item.id} className="td-ev-item">
                                <div className="td-ev-item-left">
                                  <i className={`fas ${TYPE_ICONS[item.itemType] || 'fa-file'} td-ev-item-icon`} />
                                  <div>
                                    {item.itemType === 'FILE' && (
                                      <button className="td-ev-link-btn"
                                        onClick={() => handleDownload(item.id, item.fileName)}>
                                        <i className="fas fa-download" /> {item.fileName}
                                      </button>
                                    )}
                                    {item.itemType === 'LINK' && (
                                      <a href={item.externalLink} target="_blank" rel="noreferrer" className="td-ev-ext-link">
                                        {item.externalLink}
                                      </a>
                                    )}
                                    {item.notes && <div className="td-ev-item-notes">{item.notes}</div>}
                                    <div className="td-ev-item-date">{item.submittedAt ? new Date(item.submittedAt).toLocaleString() : ''}</div>
                                  </div>
                                </div>
                                <div className="td-ev-item-right">
                                  <span className="td-ev-badge" style={{ background: REVIEW_COLORS[item.reviewStatus] || '#6b7280' }}>
                                    {item.reviewStatus}
                                  </span>
                                  {isAdmin && item.reviewStatus === 'PENDING' && (
                                    <button className="td-ev-review-btn"
                                      onClick={() => setReviewModal({ itemId: item.id, status: 'ACCEPTED', notes: '' })}>
                                      {t('TestDetails.review')}
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Submit panel */}
                        {req.status !== 'APPROVED' && (
                          <div className="td-ev-submit">
                            <div className="td-ev-type-tabs">
                              {['NOTE', 'FILE', 'LINK'].map(t => (
                                <button key={t}
                                  className={`td-ev-type-tab${isSub && submitType === t ? ' active' : ''}`}
                                  onClick={() => { setSubmitType(t); setSubmitPanel(req.id); }}>
                                  <i className={`fas ${TYPE_ICONS[t]}`} /> {t}
                                </button>
                              ))}
                            </div>
                            {isSub && submitType === 'NOTE' && (
                              <div className="td-ev-form">
                                <textarea className="td-ev-textarea" rows={3}
                                  placeholder={t('TestDetails.notePlaceholder')}
                                  value={noteText} onChange={e => setNoteText(e.target.value)} />
                                <div className="td-ev-form-actions">
                                  <button className="td-ev-btn-primary" onClick={() => handleNoteSubmit(req.id)}>{t('TestDetails.submit')}</button>
                                  <button className="td-ev-btn-ghost" onClick={() => setSubmitPanel(null)}>{t('TestDetails.cancel')}</button>
                                </div>
                              </div>
                            )}
                            {isSub && submitType === 'FILE' && (
                              <div className="td-ev-form">
                                <input type="file" className="td-ev-file"
                                  onChange={e => handleFileSubmit(req.id, e.target.files[0])} />
                                <button className="td-ev-btn-ghost" onClick={() => setSubmitPanel(null)}>{t('TestDetails.cancel')}</button>
                              </div>
                            )}
                            {isSub && submitType === 'LINK' && (
                              <div className="td-ev-form">
                                <input className="td-ev-input" placeholder="https://..."
                                  value={linkUrl} onChange={e => setLinkUrl(e.target.value)} />
                                <textarea className="td-ev-textarea" rows={2} placeholder={t('TestDetails.optionalNotes')}
                                  value={linkNote} onChange={e => setLinkNote(e.target.value)} />
                                <div className="td-ev-form-actions">
                                  <button className="td-ev-btn-primary" onClick={() => handleLinkSubmit(req.id)}>{t('TestDetails.submit')}</button>
                                  <button className="td-ev-btn-ghost" onClick={() => setSubmitPanel(null)}>{t('TestDetails.cancel')}</button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Review Modal */}
          {reviewModal && (
            <div className="ev-modal-overlay" onClick={() => setReviewModal(null)}>
              <div className="ev-modal" onClick={e => e.stopPropagation()}>
                <h3>{t('TestDetails.reviewEvidence')}</h3>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ fontSize: '0.875rem', fontWeight: 600 }}>{t('TestDetails.decision')}</label>
                  <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                    {['ACCEPTED', 'REJECTED'].map(s => (
                      <label key={s} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                        <input type="radio" value={s} checked={reviewModal.status === s}
                          onChange={() => setReviewModal(m => ({ ...m, status: s }))} />
                        <span style={{ color: REVIEW_COLORS[s], fontWeight: 600 }}>{s}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <textarea className="td-ev-textarea" rows={3} placeholder={t('TestDetails.reviewNotesPlaceholder')}
                  value={reviewModal.notes}
                  onChange={e => setReviewModal(m => ({ ...m, notes: e.target.value }))} />
                <div className="td-ev-form-actions" style={{ marginTop: '1rem' }}>
                  <button className="td-ev-btn-primary" onClick={handleReview}>{t('TestDetails.save')}</button>
                  <button className="td-ev-btn-ghost" onClick={() => setReviewModal(null)}>{t('TestDetails.cancel')}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TestDetails;
